import React, { createContext, useContext, useState, useEffect } from 'react';
import { detectCountry, setManualCountry } from '../utils/geolocation';
import { getLocaleConfig } from '../utils/localeConfig';

const LocaleContext = createContext();

/**
 * LocaleProvider Component
 * Manages country detection, language preference, and date format settings
 */
export function LocaleProvider({ children }) {
  const [country, setCountryState] = useState(null);
  const [locale, setLocale] = useState(null);
  const [language, setLanguageState] = useState('en');
  const [dateFormat, setDateFormatState] = useState('DD/MM/YYYY');
  const [loading, setLoading] = useState(true);

  // Load language and date format preferences from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('user_language');
    const savedDateFormat = localStorage.getItem('user_date_format');

    if (savedLanguage) {
      console.log(`📖 Loaded saved language preference: ${savedLanguage}`);
      setLanguageState(savedLanguage);
    }

    if (savedDateFormat) {
      console.log(`📖 Loaded saved date format preference: ${savedDateFormat}`);
      setDateFormatState(savedDateFormat);
    }
  }, []);

  // Detect country on mount
  useEffect(() => {
    async function initializeLocale() {
      setLoading(true);

      try {
        console.log('🌍 Initializing locale detection...');

        // Detect country
        const detectedCountry = await detectCountry();
        console.log(`✅ Country detected: ${detectedCountry}`);

        setCountryState(detectedCountry);

        // Get locale configuration
        const localeConfig = getLocaleConfig(detectedCountry);
        setLocale(localeConfig);

        // Set initial language based on country (if not overridden by user preference)
        const savedLanguage = localStorage.getItem('user_language');
        if (!savedLanguage) {
          // Use country's default language
          const defaultLanguage = localeConfig.language;
          console.log(`🌐 Setting default language: ${defaultLanguage} (from country)`);
          setLanguageState(defaultLanguage);
        }

        // Set initial date format based on country (if not overridden by user preference)
        const savedDateFormat = localStorage.getItem('user_date_format');
        if (!savedDateFormat) {
          // Use country's default date format
          const defaultDateFormat = localeConfig.defaultDateFormat;
          console.log(`📅 Setting default date format: ${defaultDateFormat} (from country)`);
          setDateFormatState(defaultDateFormat);
        }

      } catch (error) {
        console.error('❌ Failed to initialize locale:', error);

        // Fallback to DEFAULT configuration
        const defaultLocale = getLocaleConfig('DEFAULT');
        setCountryState('DEFAULT');
        setLocale(defaultLocale);
        setLanguageState('en');
        setDateFormatState('DD/MM/YYYY');
      } finally {
        setLoading(false);
      }
    }

    initializeLocale();
  }, []);

  /**
   * Set language preference
   * @param {string} lang - Language code ('en', 'pt', 'es')
   * @param {Function} saveToFirebaseFn - Optional Firebase save function from AuthContext
   */
  const setLanguage = (lang, saveToFirebaseFn) => {
    console.log(`🌐 Setting language: ${lang}`);
    setLanguageState(lang);

    // Save to localStorage
    localStorage.setItem('user_language', lang);

    // Save to Firebase if function provided
    if (saveToFirebaseFn && typeof saveToFirebaseFn === 'function') {
      saveToFirebaseFn({ language: lang }).catch(error => {
        console.error('Failed to save language to Firebase:', error);
      });
    }
  };

  /**
   * Set date format preference
   * @param {string} format - Date format ('MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD')
   * @param {Function} saveToFirebaseFn - Optional Firebase save function from AuthContext
   */
  const setDateFormat = (format, saveToFirebaseFn) => {
    console.log(`📅 Setting date format: ${format}`);
    setDateFormatState(format);

    // Save to localStorage
    localStorage.setItem('user_date_format', format);

    // Save to Firebase if function provided
    if (saveToFirebaseFn && typeof saveToFirebaseFn === 'function') {
      saveToFirebaseFn({ dateFormat: format }).catch(error => {
        console.error('Failed to save date format to Firebase:', error);
      });
    }
  };

  /**
   * Set country preference
   * @param {string} countryCode - Country code ('US', 'BR', 'ZA', etc.)
   * @param {Function} saveToFirebaseFn - Optional Firebase save function from AuthContext
   */
  const setCountry = (countryCode, saveToFirebaseFn) => {
    console.log(`🌍 Setting country: ${countryCode}`);

    // Update country state
    setCountryState(countryCode);

    // Get new locale configuration
    const newLocaleConfig = getLocaleConfig(countryCode);
    setLocale(newLocaleConfig);

    // Auto-update language to new country's default
    const newLanguage = newLocaleConfig.language;
    console.log(`🌐 Auto-updating language to: ${newLanguage}`);
    setLanguageState(newLanguage);
    localStorage.setItem('user_language', newLanguage);

    // Auto-update date format to new country's default
    const newDateFormat = newLocaleConfig.defaultDateFormat;
    console.log(`📅 Auto-updating date format to: ${newDateFormat}`);
    setDateFormatState(newDateFormat);
    localStorage.setItem('user_date_format', newDateFormat);

    // Save manual country selection to localStorage
    setManualCountry(countryCode);

    // Save to Firebase if function provided
    if (saveToFirebaseFn && typeof saveToFirebaseFn === 'function') {
      saveToFirebaseFn({
        country: countryCode,
        language: newLanguage,
        dateFormat: newDateFormat
      }).catch(error => {
        console.error('Failed to save country to Firebase:', error);
      });
    }
  };

  /**
   * Force set country for SEO routes (no localStorage persistence)
   * Used by country routes (/us, /ca, /uk, etc.) so Googlebot sees correct content
   * without polluting localStorage preferences
   * @param {string} countryCode - Country code ('US', 'BR', 'ZA', etc.)
   */
  const forceSetCountry = (countryCode) => {
    console.log(`🌍 Force setting country for SEO route: ${countryCode}`);

    setCountryState(countryCode);

    const newLocaleConfig = getLocaleConfig(countryCode);
    setLocale(newLocaleConfig);

    // Only set language/dateFormat from country defaults if no saved user preferences
    const savedLanguage = localStorage.getItem('user_language');
    if (!savedLanguage) {
      setLanguageState(newLocaleConfig.language);
    }

    const savedDateFormat = localStorage.getItem('user_date_format');
    if (!savedDateFormat) {
      setDateFormatState(newLocaleConfig.defaultDateFormat);
    }

    setLoading(false);
  };

  /**
   * Load user preferences from Firebase (called by AuthContext on login)
   * @param {Object} userData - User data from Firestore
   */
  const loadUserPreferences = (userData) => {
    if (userData.language) {
      console.log(`📖 Loading language from Firebase: ${userData.language}`);
      setLanguageState(userData.language);
      localStorage.setItem('user_language', userData.language);
    }

    if (userData.dateFormat) {
      console.log(`📖 Loading date format from Firebase: ${userData.dateFormat}`);
      setDateFormatState(userData.dateFormat);
      localStorage.setItem('user_date_format', userData.dateFormat);
    }

    if (userData.country) {
      console.log(`📖 Country from Firebase: ${userData.country}`);
      // Note: We don't override detected country, just log it
    }
  };

  const value = {
    country,
    locale,
    language,
    dateFormat,
    loading,
    setCountry,
    setLanguage,
    setDateFormat,
    loadUserPreferences,
    forceSetCountry
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

/**
 * Hook to use locale context
 */
export function useLocale() {
  const context = useContext(LocaleContext);

  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }

  return context;
}

export default LocaleContext;
